/** A filing's reviews, the reviewed act and the rehearsal link (B10).
 *
 * **A review is a record, not an approval.** The server says so in `what_a_review_is` and the panel
 * prints that sentence rather than a second one written here (design rule 7).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, apiUrl } from "./client";
import { unwrap } from "./queries";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type ReportReviews = components["schemas"]["ReportReviewsOut"];
export type ReportReview = components["schemas"]["ReportReviewOut"];
export type MarkedReviewed = components["schemas"]["MarkReviewedOut"];

export const reviewKeys = {
  of: (obligationId: string) => ["report-reviews", obligationId] as const,
};

/** Who has marked this filing's report as reviewed. Fetched fresh: whether a review still reads
 * the same moves whenever a figure does. */
export function useReportReviews(obligationId: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.of(obligationId ?? "none"),
    enabled: Boolean(obligationId),
    staleTime: 0,
    queryFn: async (): Promise<ReportReviews> =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}/reviews", {
          params: { path: { obligation_id: obligationId as string } },
        }),
      ),
  });
}

/** Mark the report as reviewed, on the Scope 2 basis the person is looking at. The server refuses
 * anyone but an Approver or an Admin, and a repeat over the same content writes nothing. */
export function useMarkReviewed(obligationId: string | undefined, method: "location" | "market") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<MarkedReviewed> => {
      if (!obligationId) throw new Error("no filing to review");
      const { data, error, response } = await api.POST(
        "/api/v1/reports/obligations/{obligation_id}/reviewed",
        {
          params: {
            path: { obligation_id: obligationId },
            query: { scope2_method: method },
          },
        },
      );
      if (error || !data) throw refusalFrom(response, error, "That could not be recorded.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["report-reviews"] });
      // The activity list shows the new entry.
      await queryClient.invalidateQueries({ queryKey: ["activity"] });
    },
  });
}

/** The rehearsal page for a filing, as a link to open in a new tab. A navigation, not a fetch: the
 * page is HTML the browser draws, marked as a rehearsal, with no fingerprint (the record it
 * rehearses does not exist yet). */
export function rehearsalUrl(obligationId: string, method: "location" | "market"): string {
  return apiUrl(`/api/v1/reports/obligations/${obligationId}/rehearsal?scope2_method=${method}`);
}
